<script>
  import { link, location } from 'svelte-spa-router';
  import { logout, authStore } from '../stores/auth.js';
  import { _ } from '../lib/i18n.js';
  import { Menu, Users, Home, LogOut, ChevronRight, Box, Package, FileText, Settings, Sun, Moon, Key, BarChart3, Clock, Wallet, ShieldCheck, PenTool, Handshake, KeyRound } from 'lucide-svelte';
  import { canViewProposals, canManageSettings, canViewCustomerList, canViewProductList, canViewCatalog, canViewTimesheets, canViewComplianceReports, canSignDocuments, canManagePartners, canViewEnrollment, isCustomer, isPartner, loadUserRoles } from '../stores/roles.js';
  import { PAYROLL_ENABLED } from '../lib/features.js';
  import { theme } from '../stores/theme.js';
  import { onMount } from 'svelte';

  onMount(() => {
    loadUserRoles();
    theme.init();
  });

  let isSidebarOpen = false;
  $: currentPath = $location;

  let userIdentifier = '';
  let userInitial = 'U';

  $: userIdentifier = ($authStore.user?.email ?? $authStore.user?.userId ?? '').trim();
  $: userInitial = userIdentifier.charAt(0).toUpperCase() || 'U';

  function toggleSidebar() {
    isSidebarOpen = !isSidebarOpen;
  }

  async function handleLogout() {
    await logout();
  }

  function isActiveRoute(path) {
    return currentPath === path || (path !== '/' && currentPath.startsWith(path));
  }
</script>

<div class="min-h-screen transition-colors duration-300" style="background-color: var(--color-bg-dark);">
  <!-- Mobile sidebar overlay -->
  {#if isSidebarOpen}
    <div class="fixed inset-0 z-40 lg:hidden fade-in" on:click={toggleSidebar}>
      <div class="fixed inset-0 bg-black/70"></div>
    </div>
  {/if}

  <!-- Sidebar -->
  <div class="fixed inset-y-0 left-0 z-50 w-64 sidebar-modern transform transition-all duration-300 ease-out lg:translate-x-0 {isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:block flex flex-col">
    <!-- Logo Area -->
    <div class="flex-shrink-0 flex items-center justify-center h-20 px-6 border-b-2" style="border-color: rgba(0, 240, 255, 0.2);">
      <div class="flex items-center space-x-3">
        <div class="relative">
          <img src="/webdeploy-icon.png" alt="WEBDEPLOY" class="h-10 w-auto" />
          <div class="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full animate-pulse"></div>
        </div>
        <div class="flex flex-col">
          <span class="text-xl font-bold text-gradient font-display tracking-wider">{$_('system.appName').replace(' MS', '')}</span>
          <span class="text-xs uppercase tracking-widest" style="color: var(--color-text-muted);">{$_('system.managementSystem')}</span>
        </div>
      </div>
    </div>

    <!-- Navigation (scrollable) -->
    <nav class="flex-1 overflow-y-auto px-3 py-4">
      <div class="space-y-1">
        <div class="px-4 mb-4">
          <span class="text-xs font-bold uppercase tracking-widest" style="color: var(--color-text-muted);">
            {$_('system.mainMenu')}
          </span>
        </div>

        <a
          href="/"
          use:link
          class="nav-item {isActiveRoute('/') ? 'nav-item-active' : ''}"
          on:click={() => isSidebarOpen = false}
        >
          <Home size={18} class="mr-3 flex-shrink-0" />
          <span class="flex-1">{$_('nav.home')}</span>
          {#if isActiveRoute('/')}
            <ChevronRight size={14} class="text-primary-500" />
          {/if}
        </a>

        {#if $canViewCustomerList}
          <a
            href="/customers"
            use:link
            class="nav-item {isActiveRoute('/customers') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Users size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.customers')}</span>
            {#if isActiveRoute('/customers')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canViewProductList}
          <a
            href="/products"
            use:link
            class="nav-item {isActiveRoute('/products') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Box size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.products')}</span>
            {#if isActiveRoute('/products')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canViewCatalog}
          <a
            href="/catalogo"
            use:link
            class="nav-item {isActiveRoute('/catalogo') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Package size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.catalog')}</span>
            {#if isActiveRoute('/catalogo')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canViewProposals}
          <a
            href="/proposals"
            use:link
            class="nav-item {isActiveRoute('/proposals') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <FileText size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.proposals')}</span>
            {#if isActiveRoute('/proposals')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canSignDocuments}
          <a
            href="/firma"
            use:link
            class="nav-item {isActiveRoute('/firma') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <PenTool size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.signatures')}</span>
            {#if isActiveRoute('/firma')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canManageSettings}
          <a
            href="/reports"
            use:link
            class="nav-item {isActiveRoute('/reports') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <BarChart3 size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.reports')}</span>
            {#if isActiveRoute('/reports')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canViewComplianceReports}
          <a
            href="/compliance-reports"
            use:link
            class="nav-item {isActiveRoute('/compliance-reports') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <ShieldCheck size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.complianceReports')}</span>
            {#if isActiveRoute('/compliance-reports')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canManagePartners}
          <a
            href="/partners"
            use:link
            class="nav-item {isActiveRoute('/partners') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Handshake size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.partners')}</span>
            {#if isActiveRoute('/partners')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {:else if $isPartner}
          <a
            href="/my-partner"
            use:link
            class="nav-item {isActiveRoute('/my-partner') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Handshake size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.myPartner')}</span>
            {#if isActiveRoute('/my-partner')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canViewEnrollment}
          <a
            href="/enrollment"
            use:link
            class="nav-item {isActiveRoute('/enrollment') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <KeyRound size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.enrollment')}</span>
            {#if isActiveRoute('/enrollment')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canManageSettings}
          <a
            href="/admin/tokens"
            use:link
            class="nav-item {isActiveRoute('/admin/tokens') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Key size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.ingestTokens')}</span>
            {#if isActiveRoute('/admin/tokens')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canManageSettings}
          <a
            href="/timesheets"
            use:link
            class="nav-item {isActiveRoute('/timesheets') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Clock size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.timesheets')}</span>
            {#if isActiveRoute('/timesheets')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {:else if $isCustomer}
          <a
            href="/my-timesheets"
            use:link
            class="nav-item {isActiveRoute('/my-timesheets') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Clock size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.myTimesheets')}</span>
            {#if isActiveRoute('/my-timesheets')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canManageSettings && PAYROLL_ENABLED}
          <a
            href="/payroll"
            use:link
            class="nav-item {isActiveRoute('/payroll') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Wallet size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.payroll')}</span>
            {#if isActiveRoute('/payroll')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}

        {#if $canManageSettings}
          <a
            href="/settings"
            use:link
            class="nav-item {isActiveRoute('/settings') ? 'nav-item-active' : ''}"
            on:click={() => isSidebarOpen = false}
          >
            <Settings size={18} class="mr-3 flex-shrink-0" />
            <span class="flex-1">{$_('nav.settings')}</span>
            {#if isActiveRoute('/settings')}
              <ChevronRight size={14} class="text-primary-500" />
            {/if}
          </a>
        {/if}
      </div>
    </nav>

    <!-- User Info & Logout -->
    <div class="flex-shrink-0 w-full p-4 border-t-2" style="border-color: rgba(0, 240, 255, 0.2); background-color: var(--color-surface-dark);">
      {#if $authStore.user}
        <div class="mb-3 p-3 border-2" style="border-color: var(--color-border-default); background-color: var(--color-surface-light);">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 border-2 border-primary-500 flex items-center justify-center" style="background-color: rgba(0, 240, 255, 0.1);">
              <span class="text-primary-500 font-bold text-sm">
                {userInitial}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold truncate" style="color: var(--color-text-heading);">
                {userIdentifier || $_('auth.user')}
              </p>
              <p class="text-xs" style="color: var(--color-text-muted);">{$_('auth.connectedUser')}</p>
            </div>
          </div>
        </div>
      {/if}

      <a
        href="#/change-password"
        class="flex items-center w-full px-3 py-3 text-sm font-bold tracking-wide transition-all duration-100 group mb-1 border-l-3 border-transparent hover:border-primary-500 hover:text-primary-500"
        style="color: var(--color-text-body);"
      >
        <Key size={16} class="mr-3" />
        <span>{$_('nav.changePassword')}</span>
      </a>
      <button
        on:click={handleLogout}
        class="flex items-center w-full px-3 py-3 text-sm font-bold tracking-wide transition-all duration-100 group border-l-3 border-transparent hover:border-red-500 hover:text-red-400"
        style="color: var(--color-text-body);"
      >
        <LogOut size={16} class="mr-3" />
        <span>{$_('nav.logout')}</span>
      </button>
    </div>
  </div>

  <!-- Main content -->
  <div class="lg:pl-64">
    <!-- Top bar -->
    <div class="sticky top-0 z-40 glass border-b-2" style="border-color: rgba(0, 240, 255, 0.2);">
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <button
              on:click={toggleSidebar}
              class="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center text-gray-400 hover:text-primary-500 transition-all duration-100"
            >
              <Menu size={24} />
            </button>
          </div>

          <div class="flex items-center space-x-4">
            <!-- Theme Toggle -->
            <button
              on:click={() => theme.toggle()}
              class="h-10 w-10 inline-flex items-center justify-center text-gray-400 hover:text-primary-500 transition-all duration-100 border-2 border-transparent hover:border-primary-500"
              title={$theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {#if $theme === 'dark'}
                <Sun size={18} />
              {:else}
                <Moon size={18} />
              {/if}
            </button>

            <!-- Breadcrumb indicator -->
            <div class="hidden sm:flex items-center space-x-2 text-sm font-bold uppercase tracking-wider" style="color: var(--color-text-muted);">
              {#if currentPath === '/'}
                <span class="text-primary-500 crt-glow">{$_('system.dashboard')}</span>
              {:else if currentPath.startsWith('/customers')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.customers')}</span>
              {:else if currentPath.startsWith('/products')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.products')}</span>
              {:else if currentPath.startsWith('/proposals')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.proposals')}</span>
              {:else if currentPath.startsWith('/reports')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.reports')}</span>
              {:else if currentPath.startsWith('/compliance-reports')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.complianceReports')}</span>
              {:else if currentPath.startsWith('/admin/tokens')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.ingestTokens')}</span>
              {:else if currentPath.startsWith('/timesheets')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.timesheets')}</span>
              {:else if currentPath.startsWith('/settings')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.settings')}</span>
              {:else if currentPath.startsWith('/payroll')}
                <span>{$_('system.dashboard')}</span>
                <ChevronRight size={14} />
                <span class="text-primary-500 crt-glow">{$_('nav.payroll')}</span>
              {/if}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Page content -->
    <main class="p-4 sm:p-6 lg:p-8">
      <div class="fade-in">
        <slot />
      </div>
    </main>
  </div>
</div>
